import React from 'react';
import { Box, Typography, IconButton, Tooltip, Paper } from '@mui/material';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, startOfWeek, endOfWeek } from 'date-fns';
import EventIcon from '@mui/icons-material/Event';
import TaskIcon from '@mui/icons-material/Assignment';
import CloseIcon from '@mui/icons-material/Close';

const MonthView = ({ date, items, onCellClick, onEditItem, onDeleteItem }) => {
  // Convert dayjs date to JavaScript Date object
  const currentDate = date.toDate();
  
  // Get the first day of the month
  const monthStart = startOfMonth(currentDate);
  // Get the last day of the month
  const monthEnd = endOfMonth(currentDate);
  // Get the start of the week that contains the first day of the month
  const calendarStart = startOfWeek(monthStart);
  // Get the end of the week that contains the last day of the month
  const calendarEnd = endOfWeek(monthEnd);
  
  // Get all days that should be shown in the calendar grid
  const days = eachDayOfInterval({ 
    start: calendarStart,
    end: calendarEnd 
  });

  // Group items by date
  const itemsByDate = items.reduce((acc, item) => {
    const dateKey = format(new Date(item.start), 'yyyy-MM-dd');
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(item);
    return acc;
  }, {});

  return (
    <Box sx={{ width: '100%', height: '100%', p: 2 }}>
      {/* Days of week header */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 1,
        mb: 1 
      }}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <Typography 
            key={day} 
            variant="subtitle2" 
            sx={{ 
              textAlign: 'center',
              fontWeight: 600,
              color: '#666'
            }}
          >
            {day}
          </Typography>
        ))}
      </Box>

      {/* Calendar grid */}
      <Box sx={{ 
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 1,
        height: 'calc(100% - 48px)'
      }}>
        {days.map(day => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const dayItems = itemsByDate[dateKey] || [];
          const isCurrentMonth = isSameMonth(day, currentDate);
          const isCurrentDay = isToday(day);

          return (
            <Paper
              key={dateKey}
              elevation={0}
              onClick={() => onCellClick(dateKey)}
              sx={{
                p: 1,
                height: '100%',
                minHeight: 120,
                cursor: 'pointer',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                bgcolor: isCurrentDay ? '#f3f4ff' : '#fff',
                opacity: isCurrentMonth ? 1 : 0.5,
                '&:hover': {
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              <Typography 
                variant="subtitle2" 
                sx={{ 
                  mb: 1,
                  fontWeight: isCurrentDay ? 700 : 500,
                  color: isCurrentDay ? '#3730a3' : '#666'
                }}
              >
                {format(day, 'd')}
              </Typography>
              
              <Box sx={{ overflow: 'auto', maxHeight: 'calc(100% - 28px)' }}>
                {dayItems.map(item => (
                  <Box
                    key={item.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      mb: 0.5,
                      p: 0.5,
                      borderRadius: 1,
                      bgcolor: item.type === 'agenda' ? '#e0e7ff' : '#fef9c3',
                      color: item.type === 'agenda' ? '#3730a3' : '#92400e',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      cursor: 'pointer',
                      '&:hover': {
                        filter: 'brightness(0.95)'
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditItem(item);
                    }}
                  >
                    {item.type === 'agenda' ? (
                      <EventIcon sx={{ fontSize: 16 }} />
                    ) : (
                      <TaskIcon sx={{ fontSize: 16 }} />
                    )}
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 'inherit',
                        fontWeight: 'inherit',
                        flex: 1
                      }}
                    >
                      {item.title}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteItem(item.id);
                      }}
                      sx={{
                        p: 0.25,
                        opacity: 0,
                        '&:hover': {
                          bgcolor: 'transparent',
                          opacity: 1,
                        },
                        '.MuiBox-root:hover &': {
                          opacity: 0.7,
                        }
                      }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
};

export default MonthView; 